/**
 * El paginado de una lista que ya está en memoria.
 *
 * Aquí sólo las cuentas, sin React: qué página toca, cuántas hay y qué renglones
 * caen en ella. El estado lo lleva `usePagination` y el pie lo pinta
 * `Pagination`, en el sistema de diseño.
 *
 * Es paginado **de lo ya cargado**. Los listados del portal piden su lista
 * entera —las personas de un área, las solicitudes de alguien— y lo que se
 * volvía tedioso no era cargarla sino recorrerla: 134 renglones en fila en
 * Creación de contenidos. La bitácora es la excepción, porque sí pide por
 * tandas al servidor; ver `AuditScreen`.
 */

/** Cuántos renglones por página se pueden elegir. El primero es el de fábrica. */
export const PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

export interface PageWindow<T> {
  /** Los renglones de esta página. */
  readonly items: readonly T[];
  /** La página que se enseña, desde 1. Puede no ser la pedida: ver `pageWindow`. */
  readonly page: number;
  readonly pageCount: number;
  /** El primero y el último que se ven, contando desde 1. Cero si no hay nada. */
  readonly from: number;
  readonly to: number;
  readonly total: number;
}

/**
 * Lo que necesita el pie para pintarse: dónde se está y cómo moverse. Es
 * `usePagination` sin los renglones, que al pie no le hacen falta.
 */
export interface PagerControls {
  readonly page: number;
  readonly pageCount: number;
  readonly from: number;
  readonly to: number;
  readonly total: number;
  readonly pageSize: number;
  /** Si hay algo que paginar. Con `false` el pie no se pinta. */
  readonly needed: boolean;
  /**
   * Si hay más renglones de los que `total` cuenta.
   *
   * Sólo la bitácora lo usa: se pide al servidor por tandas, y cuando éste no
   * puede decir cuántos movimientos hay, el total es lo ya traído. El pie dice
   * entonces «más de» y no afirma una última página que no conoce.
   */
  readonly open?: boolean;
  readonly goTo: (page: number) => void;
  readonly setPageSize: (size: number) => void;
}

/**
 * La ventana de una lista.
 *
 * **La página se ajusta a lo que hay**, y es lo que evita la pantalla vacía:
 * quien está en la página 9 y filtra hasta dejar doce renglones no se queda
 * mirando una novena página que ya no existe, cae en la última que sí.
 */
export const pageWindow = <T>(all: readonly T[], page: number, size: number): PageWindow<T> => {
  const total = all.length;
  const perPage = Math.max(1, Math.floor(size));
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(Math.max(1, Math.floor(page)), pageCount);
  const start = (current - 1) * perPage;
  const items = all.slice(start, start + perPage);

  return {
    items,
    page: current,
    pageCount,
    from: total === 0 ? 0 : start + 1,
    to: start + items.length,
    total,
  };
};

/**
 * Si una lista necesita pie de paginado.
 *
 * Con diez renglones o menos no hay nada que paginar ni nada que elegir: el pie
 * sería un «1–7 de 7» con dos botones apagados. Se mira el total y no las
 * páginas, porque quien eligió «50 por página» sobre treinta renglones tiene una
 * sola página y sigue necesitando el selector para volver a diez.
 */
export const needsPaging = (total: number): boolean => total > DEFAULT_PAGE_SIZE;
